import React, { useEffect, useCallback, useState, useRef } from 'react';
import * as d3 from 'd3';

export interface ZoomOptions {
  scaleExtent?: [number, number];
  translateExtent?: [[number, number], [number, number]];
  extent?: [[number, number], [number, number]];
  filter?: (event: WheelEvent | MouseEvent | TouchEvent) => boolean;
  duration?: number;
  enableDoubleClickReset?: boolean;
}

interface ZoomState {
  k: number;  // ズームレベル
  x: number;  // X軸の平行移動
  y: number;  // Y軸の平行移動
}

/**
 * D3.js用のズーム・パン機能を管理するカスタムフック
 * 
 * @param svgRef SVG要素のref
 * @param onZoom ズーム時のコールバック関数
 * @param options ズームのオプション設定
 * @returns ズームの状態とコントロール関数
 * 
 * @example
 * ```typescript
 * const svgRef = useRef<SVGSVGElement>(null);
 * const { zoomState, resetZoom, zoomIn, zoomOut } = useD3Zoom(
 *   svgRef,
 *   (transform) => {
 *     // スケールを更新
 *     const newXScale = transform.rescaleX(originalXScale);
 *     const newYScale = transform.rescaleY(originalYScale);
 *   },
 *   { scaleExtent: [0.5, 10] }
 * );
 * ```
 */
export function useD3Zoom(
  svgRef: React.RefObject<SVGSVGElement | null>,
  onZoom?: (transform: d3.ZoomTransform) => void,
  options: ZoomOptions = {}
) {
  const {
    scaleExtent = [1, 10],
    translateExtent,
    extent,
    filter,
    duration = 750,
    enableDoubleClickReset = true
  } = options;

  const [zoomState, setZoomState] = useState<ZoomState>({ k: 1, x: 0, y: 0 });
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | undefined>(undefined);

  // デフォルトのフィルター関数
  const defaultFilter = useCallback((event: WheelEvent | MouseEvent | TouchEvent) => {
    // ホイール操作はCtrl/Cmdキーを押している時のみ
    if (event.type === 'wheel') {
      return (event as WheelEvent).ctrlKey || (event as WheelEvent).metaKey;
    }
    // ドラッグ操作は常に許可（パン用）
    if (event.type === 'mousedown' || event.type === 'mousemove') {
      return !(event as MouseEvent).button; // 左クリックのみ
    }
    // タッチ操作は常に許可
    return true;
  }, []);

  // ズームビヘイビアの初期化
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    
    // 既存のズームをクリア
    svg.on('.zoom', null);

    // ズームビヘイビアを作成
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent(scaleExtent);

    // オプション設定
    if (translateExtent) {
      zoom.translateExtent(translateExtent);
    }
    if (extent) {
      zoom.extent(extent);
    }
    
    zoom.filter(filter || defaultFilter);

    // ズームイベントハンドラー
    zoom.on('zoom', (event) => {
      const transform = event.transform;
      
      // 状態を更新
      setZoomState({
        k: transform.k,
        x: transform.x,
        y: transform.y
      });

      // コールバックを呼び出し
      if (onZoom) {
        onZoom(transform);
      }
    });

    // SVGにズームを適用
    svg.call(zoom);

    // ダブルクリックでリセット
    if (enableDoubleClickReset) {
      svg.on('dblclick.zoom', () => {
        svg.transition()
          .duration(duration)
          .call(zoom.transform, d3.zoomIdentity);
      });
    }

    zoomBehaviorRef.current = zoom;

    // クリーンアップ
    return () => {
      svg.on('.zoom', null);
      if (enableDoubleClickReset) {
        svg.on('dblclick.zoom', null);
      }
    };
  }, [svgRef, onZoom, scaleExtent, translateExtent, extent, filter, defaultFilter, duration, enableDoubleClickReset]);

  // ズームをリセット
  const resetZoom = useCallback(() => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.transition()
      .duration(duration)
      .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
  }, [svgRef, duration]);

  // ズームイン
  const zoomIn = useCallback(() => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.transition()
      .duration(duration)
      .call(zoomBehaviorRef.current.scaleBy, 1.5);
  }, [svgRef, duration]);

  // ズームアウト
  const zoomOut = useCallback(() => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.transition()
      .duration(duration)
      .call(zoomBehaviorRef.current.scaleBy, 0.67);
  }, [svgRef, duration]);

  // 指定した領域にズーム
  const zoomToExtent = useCallback((x0: number, y0: number, x1: number, y1: number) => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;

    const svg = d3.select(svgRef.current);
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // ズーム領域の中心と大きさを計算
    const dx = x1 - x0;
    const dy = y1 - y0;
    const x = (x0 + x1) / 2;
    const y = (y0 + y1) / 2;

    // 必要なスケールを計算
    const scale = Math.max(1, Math.min(8, 0.9 / Math.max(dx / width, dy / height)));

    // トランスフォームを適用
    const transform = d3.zoomIdentity
      .translate(width / 2, height / 2)
      .scale(scale)
      .translate(-x, -y);

    svg.transition()
      .duration(duration)
      .call(zoomBehaviorRef.current.transform, transform);
  }, [svgRef, duration]);

  // プログラムからズーム状態を設定
  const setZoom = useCallback((k: number, x = 0, y = 0) => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;

    const svg = d3.select(svgRef.current);
    const transform = d3.zoomIdentity
      .translate(x, y)
      .scale(k);

    svg.call(zoomBehaviorRef.current.transform, transform);
  }, [svgRef]);

  return {
    zoomState,
    resetZoom,
    zoomIn,
    zoomOut,
    zoomToExtent,
    setZoom,
    zoomBehavior: zoomBehaviorRef.current
  };
}